import { type ReactNode, type ReactElement } from 'react'
import { ButtonEdit, Page, TAlert, TButton } from '@components'
import { formatDate, stringifyGameAnnounceDate, useUpdateGame, useTranslation } from '@front/model'
import { useToastError } from '@hooks'
import { Group, Stack, Text } from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { Link, createFileRoute } from '@tanstack/react-router'
import { hasStarted, type GameData } from '@terros/common'
import { CompetitionPageContainer } from '../competition/CompetitionPageContainer'
import { useGameContext } from '../competition_.$gameId/useGameContext'
import { GameAuditInfo } from './AuditInfo'
import { Awards } from './Awards'
import { BasicInfo } from './BasicInfo'
import { CloneGameButton } from './CloneGameButton'
import { Participant } from './Participant'
import { Scoring } from './Scoring'
import { Timeline } from './Timeline'
import { GameActions } from './actions'
import { getGameWarning } from './util'

function CompetitionSummary(): ReactElement {
  const { t } = useTranslation()
  const { data, refetch, isFetching } = useGameContext()

  const warning = getGameWarning(data)

  return (
    <CompetitionPageContainer>
      <Page.Heading isFetching={isFetching} refetch={refetch} title={t('Summary')}>
        <TButton component={Link} icon='visibility' to={`/competition/${data.game.gameId}/view`} variant='secondary'>
          {hasStarted(data.game) ? t('View Competition') : t('Competition Preview')}
        </TButton>
        <CloneGameButton />
      </Page.Heading>
      <Page.Content scrollArea>
        {warning ? (
          <TAlert flex={1} title={warning.title} variant='warning'>
            <Group>
              <Text>{warning.message}</Text>
              <ButtonEdit component={Link} to={warning.link} variant='secondary' />
            </Group>
          </TAlert>
        ) : (
          <CongratulationMessage game={data.game} />
        )}
        <BasicInfo />
        <Timeline />
        <Participant />
        <Scoring />
        <Awards />
        <GameAuditInfo />
        <GameActions game={data.game} />
      </Page.Content>
    </CompetitionPageContainer>
  )
}

const CongratulationMessage = ({ game }: { game: GameData }): ReactNode => {
  const { t } = useTranslation()

  if (game.status === 'published') {
    return (
      <TAlert title={t('Your competition has been announced!')}>
        {t('Your competition has been announced and will begin on {{date}}', { date: formatDate(game.startDate) })}
      </TAlert>
    )
  }

  if (game.status === 'draft') {
    return (
      <TAlert title={t('You have completed setting up your competition!')}>
        <Stack>
          {t('Terros will automatically notify participants on {{date}}', { date: stringifyGameAnnounceDate(game) })}
          <PublishButton />
        </Stack>
      </TAlert>
    )
  }
}

const PublishButton = (): ReactNode => {
  const { t } = useTranslation()
  const { data } = useGameContext()
  const { update, isUpdating, error } = useUpdateGame()
  useToastError(error)

  if (data.game.status !== 'draft') return

  const publish = async (): Promise<void> => {
    await update({
      ...data.game,
      status: 'published',
    })
    showNotification({
      title: t('Game Announced'),
      message: t('Your game has been announced'),
      color: 'green',
    })
  }
  return (
    <TButton icon='campaign' loading={isUpdating} variant='secondary' onClick={publish}>
      {t('Announce Now Instead')}
    </TButton>
  )
}

export const Route = createFileRoute('/competition_/$gameId/summary/')({
  component: CompetitionSummary,
})
